import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { usePaqueteDeLaEmpresa, useSesion } from '../../sesion/ProveedorDeSesion';
import { buscar } from './buscar';
import { GRUPOS, INICIO, seccionVisible, type Seccion } from './secciones';

interface Candidata {
  readonly seccion: Seccion;
  readonly nombre: string;
  readonly grupo: string;
}

/**
 * Ir a cualquier pantalla escribiendo.
 *
 * ## Por qué existe
 *
 * Agrupar el menú ordenó la casa para quien está aprendiendo, y **le agregó un costo a quien ya
 * sabe dónde está todo**: ahora hay que recorrer un grupo con la vista antes de encontrar el
 * enlace. Esto lo devuelve: dos letras y Enter.
 *
 * ## Dos decisiones
 *
 * 1. **Sólo lista lo que la persona puede abrir.** No es cosmética: un buscador que ofrece
 *    pantallas que dan error al entrar es peor que no tenerlo, porque enseña a desconfiar de lo que
 *    sugiere. Se filtra con los mismos permisos que el menú.
 * 2. **Se abre con Ctrl/⌘ + K y también con la barra del menú**, porque un atajo que nadie descubre
 *    no existe. La combinación se elige por costumbre: es la que usan las herramientas donde la
 *    gente ya aprendió el gesto.
 */
export function IrA(): React.ReactElement {
  const { t } = useTranslation();
  const { permisos } = useSesion();
  const tieneFiscoElectronico = usePaqueteDeLaEmpresa()?.tieneFiscoElectronico ?? true;
  const navegar = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [elegida, setElegida] = useState(0);
  const dialogo = useRef<HTMLDialogElement>(null);
  const campo = useRef<HTMLInputElement>(null);

  const candidatas = useMemo<Candidata[]>(() => {
    const puede = (seccion: Seccion): boolean =>
      seccionVisible(seccion, permisos, tieneFiscoElectronico);
    return [
      { seccion: INICIO, nombre: t(INICIO.clave), grupo: '' },
      ...GRUPOS.flatMap((grupo) =>
        grupo.secciones.filter(puede).map((seccion) => ({
          seccion,
          nombre: t(seccion.clave),
          grupo: t(grupo.clave),
        })),
      ),
    ];
  }, [permisos, t, tieneFiscoElectronico]);

  const encontradas = useMemo(() => buscar(candidatas, texto), [candidatas, texto]);

  useEffect(() => {
    const alTeclear = (evento: KeyboardEvent): void => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        setAbierto(true);
      }
    };
    globalThis.addEventListener('keydown', alTeclear);
    return () => {
      globalThis.removeEventListener('keydown', alTeclear);
    };
  }, []);

  useEffect(() => {
    const elemento = dialogo.current;
    if (elemento === null) return;
    if (abierto && !elemento.open) {
      elemento.showModal();
      setTexto('');
      setElegida(0);
      campo.current?.focus();
    }
    if (!abierto && elemento.open) elemento.close();
  }, [abierto]);

  const ir = (seccion: Seccion | undefined): void => {
    if (seccion === undefined) return;
    setAbierto(false);
    void navegar({ to: seccion.ruta });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setAbierto(true);
        }}
        className="rounded-marco border border-borde px-2 py-1 text-xs text-texto-tenue hover:bg-acento-suave"
      >
        {t('navegacion.irA')}
      </button>

      <dialog
        ref={dialogo}
        onClose={() => {
          setAbierto(false);
        }}
        aria-label={t('navegacion.irA')}
        className="w-full max-w-md rounded-marco border border-borde bg-superficie p-0 text-texto backdrop:bg-black/40"
      >
        {/*
          El contenido existe **sólo mientras está abierto**. Dejar veintisiete botones escondidos
          en cada pantalla no es gratis: quedan en el árbol de accesibilidad, un lector de pantalla
          los recorre, y cualquier búsqueda por rol los encuentra antes que a los de verdad —el
          humo se rompió por eso, y le puede pasar a cualquiera—.
        */}
        {!abierto ? null : (
          <>
            <input
              ref={campo}
              value={texto}
              aria-label={t('navegacion.irA')}
              placeholder={t('navegacion.irAAyuda')}
              onChange={(evento) => {
                setTexto(evento.target.value);
                setElegida(0);
              }}
              onKeyDown={(evento) => {
                // Las flechas mueven y Enter entra: la mano no se va del teclado, que es de lo que se
                // trata. `preventDefault` en las flechas evita que el cursor salte adentro del campo.
                if (evento.key === 'ArrowDown') {
                  evento.preventDefault();
                  setElegida((previa) => Math.min(previa + 1, encontradas.length - 1));
                }
                if (evento.key === 'ArrowUp') {
                  evento.preventDefault();
                  setElegida((previa) => Math.max(previa - 1, 0));
                }
                if (evento.key === 'Enter') {
                  evento.preventDefault();
                  ir(encontradas[elegida]?.seccion);
                }
              }}
              className="w-full border-b border-borde bg-transparent px-4 py-3 text-sm outline-none"
            />
            <ul className="max-h-80 overflow-y-auto py-1">
              {encontradas.length === 0 ? (
                <li className="px-4 py-3 text-sm text-texto-tenue">{t('navegacion.irASinNada')}</li>
              ) : (
                encontradas.map((una, posicion) => (
                  <li key={una.seccion.ruta}>
                    <button
                      type="button"
                      onMouseEnter={() => {
                        setElegida(posicion);
                      }}
                      onClick={() => {
                        ir(una.seccion);
                      }}
                      className={`flex w-full items-baseline justify-between gap-3 px-4 py-2 text-left text-sm ${
                        posicion === elegida
                          ? 'bg-acento-suave text-acento'
                          : 'hover:bg-acento-suave'
                      }`}
                    >
                      <span>{una.nombre}</span>
                      <span className="text-xs text-texto-tenue">{una.grupo}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </dialog>
    </>
  );
}
